import type {
  SupabaseTableHook,
  SupabaseTableHookQueryOperators,
  VirtualTableScope
} from '../../custom_types'
import type { Json, Tables } from '../../db_types'
import { getVirtualTableScopeColumn } from '../../virtual-tables'
import {
  canonicalizeSourceAwareJsonLogic,
  getRecordQueryFieldPath,
  getRelationalCustomFieldDescriptor,
  type RelationalCustomFieldDescriptor
} from '../../record'
import type { DataView, DataViewCreateParams, DataViewUpdateParams, DolfinRpc } from '../../rpc'
import {
  type SourceGridExpressionFilter,
  type SourceGridField,
  type SourceGridFilterExpression,
  type SourceGridFilterOperator,
  type SourceGridValueType
} from '../source-grid'
import { z } from 'zod'

export const DataViewSourceSchema = z.enum(['deal', 'activity', 'invoice', 'kpi', 'custom_record'])
export type DataViewSource = z.infer<typeof DataViewSourceSchema>

export const DATA_VIEW_SOURCE_FIELDS: Record<
  DataViewSource,
  Array<{ value: string; label: string; type: 'user' | 'date' | 'text' }>
> = {
  deal: [
    { value: 'owner_id', label: 'Owner', type: 'user' },
    { value: 'close_date', label: 'Close date', type: 'date' },
    { value: 'created_at', label: 'Created at', type: 'date' }
  ],
  activity: [
    { value: 'owner_id', label: 'Owner', type: 'user' },
    { value: 'activity_date', label: 'Activity date', type: 'date' }
  ],
  invoice: [
    { value: 'deal.owner_id', label: 'Owner', type: 'user' },
    { value: 'effective_at', label: 'Effective at', type: 'date' },
    { value: 'created_at', label: 'Created at', type: 'date' }
  ],
  kpi: [
    { value: 'profile_id', label: 'User', type: 'user' },
    { value: 'period_start', label: 'Period start', type: 'date' }
  ],
  custom_record: [
    { value: 'owner_id', label: 'Owner', type: 'user' },
    { value: 'created_at', label: 'Created at', type: 'date' }
  ]
}

const DataViewFieldsSchema = z.object({
  label: z.string().trim().min(1).max(160),
  source: DataViewSourceSchema,
  kpis_table_id: z.number().int().positive().nullable().optional(),
  custom_record_table_id: z.number().int().positive().nullable().optional(),
  filters: z.record(z.string(), z.unknown()).default({}),
  user_field: z.string().trim().min(1).max(120),
  date_field: z.string().trim().min(1).max(120)
})

const JSON_LOGIC_TO_GRID_OPERATOR: Record<string, SourceGridFilterOperator> = {
  '==': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '<': 'lt',
  '>=': 'gte',
  '<=': 'lte',
  contains: 'contains',
  doesNotContain: 'not_contains',
  startswith: 'starts_with',
  endswith: 'ends_with',
  samemonth: 'same_month',
  samequarter: 'same_quarter',
  futurequarter: 'future_quarter',
  pastquarter: 'past_quarter',
  sameyear: 'same_year',
  in: 'includes'
}
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const DATA_VIEW_SUPPORTED_META_FIELDS: Record<string, true> = { $hql: true }

const isJsonLogicVar = (value: unknown): value is { var: string } =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length === 1 &&
  'var' in value &&
  typeof value.var === 'string'

const MAX_FILTER_DEPTH = 20
const MAX_FILTER_OPERANDS = 100

const isValidDataViewFilter = (value: unknown, depth = 0): boolean => {
  if (depth > MAX_FILTER_DEPTH) return false
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const entries = Object.entries(value)
  if (entries.length === 0) return true
  if (entries.length !== 1) return false
  const [operator, operands] = entries[0]
  if (operator === 'and' || operator === 'or') {
    return (
      Array.isArray(operands) &&
      operands.length > 0 &&
      operands.length <= MAX_FILTER_OPERANDS &&
      operands.every(item => isValidDataViewFilter(item, depth + 1))
    )
  }
  return (
    JSON_LOGIC_TO_GRID_OPERATOR[operator] !== undefined &&
    Array.isArray(operands) &&
    operands.length === 2 &&
    isJsonLogicVar(operands[0])
  )
}

export type DataViewCustomFieldDefinition = Pick<
  Tables<'custom_fields'>,
  'custom_record_table_id' | 'id' | 'kpis_table_id' | 'name' | 'table_name' | 'type'
>

export type DataViewFilterFieldDefinition = {
  name: string
  type: string
}

export type DataViewCompileOptions = {
  customFields?: DataViewCustomFieldDefinition[]
  fields?: DataViewFilterFieldDefinition[]
  virtualTableScope?: VirtualTableScope | null
}
type ResolvedCustomField = {
  definition: DataViewCustomFieldDefinition
  descriptor: RelationalCustomFieldDescriptor
}

const normalizeValueType = (type: string): SourceGridValueType | null => {
  if (type === 'string') return 'text'
  if (
    type === 'text' ||
    type === 'number' ||
    type === 'boolean' ||
    type === 'date' ||
    type === 'user'
  ) {
    return type
  }
  return null
}

const resolveCustomField = (
  source: DataViewSource,
  field: string,
  options: DataViewCompileOptions
): ResolvedCustomField | null => {
  for (const definition of options.customFields ?? []) {
    const descriptor = getRelationalCustomFieldDescriptor({
      source,
      field,
      definition,
      virtualTableScope: options.virtualTableScope
    })
    if (descriptor) return { definition, descriptor }
  }
  return null
}

const findFieldDefinition = (
  source: DataViewSource,
  field: string,
  queryPath: string,
  options: DataViewCompileOptions
) =>
  (options.fields ?? []).find(definition => {
    if (definition.name === field) return true
    try {
      return getRecordQueryFieldPath(source, definition.name) === queryPath
    } catch {
      return false
    }
  })

const toSourceGridField = (
  source: DataViewSource,
  field: string,
  options: DataViewCompileOptions
): SourceGridField => {
  const customField = resolveCustomField(source, field, options)
  if (customField) {
    const valueType = normalizeValueType(customField.definition.type)
    if (!valueType) throw new Error(`Field ${field} cannot be filtered relationally`)
    return {
      kind: 'custom',
      sourceRole: customField.descriptor.sourceRole as 'local' | 'parent_deal',
      customFieldId: customField.descriptor.customFieldId,
      valueType
    }
  }

  if (
    field.split('.').some(segment => segment.startsWith('$')) &&
    !DATA_VIEW_SUPPORTED_META_FIELDS[field]
  ) {
    throw new Error(`Field ${field} is not available for this source`)
  }

  const queryPath = getRecordQueryFieldPath(source, field)
  if (!queryPath) throw new Error(`Field ${field} is not available for this source`)
  const definition = findFieldDefinition(source, field, queryPath, options)
  const isHql = field === '$hql' || queryPath === 'meta->>hql'
  const isTeamId = field === 'team_id' || field === 'deal.team_id'
  const valueType = isHql
    ? 'boolean'
    : isTeamId
      ? 'number'
      : normalizeValueType(definition?.type ?? '')
  if (!valueType) throw new Error(`Field ${field} has an unsupported filter type`)

  const parentPrefix = queryPath.startsWith('deals.')
  const localPath = parentPrefix ? queryPath.slice('deals.'.length) : queryPath
  const sourceRole = parentPrefix ? ('parent_deal' as const) : ('local' as const)
  if (localPath.startsWith('meta->>')) {
    return {
      kind: 'meta',
      sourceRole,
      field: localPath.slice('meta->>'.length),
      valueType
    }
  }
  if (localPath.includes('.')) throw new Error(`Field ${field} is not available for this grid`)
  return { kind: 'native', sourceRole, field: localPath, valueType }
}

const ALLOWED_OPERATORS: Record<
  SourceGridValueType,
  Partial<Record<SourceGridFilterOperator, true>>
> = {
  text: {
    eq: true,
    neq: true,
    contains: true,
    not_contains: true,
    starts_with: true,
    ends_with: true,
    includes: true,
    excludes: true
  },
  number: {
    eq: true,
    neq: true,
    gt: true,
    lt: true,
    gte: true,
    lte: true,
    includes: true,
    excludes: true
  },
  boolean: { eq: true, neq: true },
  date: {
    eq: true,
    neq: true,
    gt: true,
    lt: true,
    gte: true,
    lte: true,
    same_month: true,
    same_quarter: true,
    future_quarter: true,
    past_quarter: true,
    same_year: true
  },
  user: { eq: true, neq: true, includes: true, excludes: true }
}

const assertLiteralMatchesField = (
  field: SourceGridField,
  value: unknown,
  operator: SourceGridFilterOperator
) => {
  if (Array.isArray(value)) {
    if (value.length === 0 || value.length > 100)
      throw new Error('List filters require 1 to 100 values')
    value.forEach(item => assertLiteralMatchesField(field, item, operator))
    return
  }
  if (value === null) {
    if (operator !== 'eq' && operator !== 'neq') {
      throw new Error(
        `Field ${'field' in field ? field.field : field.customFieldId} only supports null equality filters`
      )
    }
    return
  }
  if (field.valueType === 'number' && typeof value !== 'number') {
    throw new Error(
      `Field ${'field' in field ? field.field : field.customFieldId} requires a number`
    )
  }
  if (field.valueType === 'boolean' && typeof value !== 'boolean') {
    throw new Error(
      `Field ${'field' in field ? field.field : field.customFieldId} requires a boolean`
    )
  }
  if ((field.valueType === 'text' || field.valueType === 'date') && typeof value !== 'string') {
    throw new Error(`Field ${'field' in field ? field.field : field.customFieldId} requires text`)
  }
  if (
    field.valueType === 'date' &&
    typeof value === 'string' &&
    !z.string().date().safeParse(value).success
  ) {
    throw new Error(
      `Field ${'field' in field ? field.field : field.customFieldId} requires an ISO date`
    )
  }
  if (field.valueType === 'user' && (typeof value !== 'string' || !UUID_PATTERN.test(value))) {
    throw new Error(
      `Field ${'field' in field ? field.field : field.customFieldId} requires a user ID`
    )
  }
}

const compileDataViewExpression = (
  rule: unknown,
  source: DataViewSource,
  options: DataViewCompileOptions,
  state: { predicates: number },
  depth = 0
): SourceGridFilterExpression => {
  if (depth > MAX_FILTER_DEPTH) {
    throw new Error('Views support at most 20 nested filter groups')
  }
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
    throw new Error('View filters must be JSON Logic objects')
  }
  const entries = Object.entries(rule)
  if (entries.length === 0) return { kind: 'group', operator: 'and', filters: [] }
  if (entries.length !== 1) throw new Error('Each view filter must contain one operator')
  const [jsonOperator, operands] = entries[0]
  if (jsonOperator === 'and' || jsonOperator === 'or') {
    if (
      !Array.isArray(operands) ||
      operands.length === 0 ||
      operands.length > MAX_FILTER_OPERANDS
    ) {
      throw new Error(`${jsonOperator} filters require 1 to ${MAX_FILTER_OPERANDS} conditions`)
    }
    return {
      kind: 'group',
      operator: jsonOperator,
      filters: operands.map(item =>
        compileDataViewExpression(item, source, options, state, depth + 1)
      )
    }
  }
  if (
    !JSON_LOGIC_TO_GRID_OPERATOR[jsonOperator] ||
    !Array.isArray(operands) ||
    operands.length !== 2
  ) {
    throw new Error(`Unsupported view filter operator ${jsonOperator}`)
  }
  if (!isJsonLogicVar(operands[0]))
    throw new Error('View filter fields must use JSON Logic var operands')
  if (state.predicates >= 100) {
    throw new Error('Views support at most 100 filter conditions')
  }
  state.predicates += 1

  const operator = JSON_LOGIC_TO_GRID_OPERATOR[jsonOperator]
  const right = operands[1]

  const field = toSourceGridField(source, operands[0].var, options)
  if (!ALLOWED_OPERATORS[field.valueType][operator]) {
    throw new Error(`Operator ${jsonOperator} is not valid for ${field.valueType} fields`)
  }

  if (isJsonLogicVar(right)) {
    const rightField = toSourceGridField(source, right.var, options)
    if (rightField.valueType !== field.valueType) {
      throw new Error('Field-to-field filters require matching field types')
    }
    return {
      kind: 'predicate',
      field,
      operator,
      operand: { kind: 'field', field: rightField }
    }
  }

  const normalizedRight =
    right === '' &&
    (field.valueType === 'date' || field.valueType === 'user') &&
    (operator === 'eq' || operator === 'neq')
      ? null
      : right
  assertLiteralMatchesField(field, normalizedRight, operator)
  return {
    kind: 'predicate',
    field,
    operator,
    operand: {
      kind: 'literal',
      value: normalizedRight as null | string | number | boolean | Array<string | number>
    }
  }
}

export function compileDataViewFilters(
  filters: Record<string, unknown>,
  source: DataViewSource,
  options: DataViewCompileOptions
): SourceGridExpressionFilter {
  return {
    kind: 'expression',
    expression: compileDataViewExpression(filters, source, options, {
      predicates: 0
    })
  }
}

const toDataViewPredicate = (
  operator: string,
  column: string,
  value: unknown
): Record<string, unknown> | null => {
  const field = { var: column }
  if (operator === 'contains' && typeof value === 'string') {
    return { contains: [field, value.replace(/^%|%$/g, '')] }
  }
  if (operator === 'in' && Array.isArray(value)) return { in: [field, value] }
  if (operator === 'eq') return { '==': [field, value] }
  if (operator === 'neq') return { '!=': [field, value] }
  if (operator === 'gt') return { '>': [field, value] }
  if (operator === 'lt') return { '<': [field, value] }
  if (operator === 'gte') return { '>=': [field, value] }
  if (operator === 'lte') return { '<=': [field, value] }
  return null
}
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const toIsoDateInTimeZone = (value: unknown, timeZone: string): unknown => {
  if (typeof value !== 'string' || ISO_DATE_PATTERN.test(value)) return value

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(parsed)
    const dateParts = Object.fromEntries(
      parts
        .filter(part => part.type === 'year' || part.type === 'month' || part.type === 'day')
        .map(part => [part.type, part.value])
    )
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}`
  } catch {
    return value
  }
}

const isSourceDateField = (source: DataViewSource, column: string) => {
  const sourceColumn = column.startsWith('deals.') ? column.slice('deals.'.length) : column
  return DATA_VIEW_SOURCE_FIELDS[source].some(
    field => field.type === 'date' && (field.value === column || field.value === sourceColumn)
  )
}

const toDataViewFieldName = (source: DataViewSource, column: string) => {
  if (column === 'profiles.teams.id' || column === 'profiles.teams_profiles.team_id') {
    return source === 'invoice' ? 'deal.team_id' : 'team_id'
  }
  if (column.startsWith('deals.meta->>')) return `deal.$${column.slice('deals.meta->>'.length)}`
  if (column.startsWith('deals.')) return `deal.${column.slice('deals.'.length)}`
  if (column.startsWith('meta->>')) return `$${column.slice('meta->>'.length)}`
  return column
}
const normalizeTeamIdFilterValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeTeamIdFilterValue)
  if (typeof value !== 'string' || value.trim() === '') return value

  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : value
}

const normalizeDataViewFilterValue = (source: DataViewSource, column: string, value: unknown) => {
  const field = toDataViewFieldName(source, column)
  return field === 'team_id' || field === 'deal.team_id' ? normalizeTeamIdFilterValue(value) : value
}

const parseNotInValues = (value: string | string[]) =>
  (Array.isArray(value) ? value : value.replace(/^\(/, '').replace(/\)$/, '').split(','))
    .map(String)
    .filter(Boolean)

const getCustomFieldReference = (
  filter: NonNullable<SupabaseTableHookQueryOperators['customFieldFilters']>[number],
  options: Pick<DataViewCompileOptions, 'customFields'>
) => {
  const definition = options.customFields?.find(field => field.id === filter.customFieldId)
  if (!definition) throw new Error(`Custom field ${filter.customFieldId} is unavailable`)
  return `${filter.sourceRole === 'parent_deal' ? 'deal.' : ''}$${definition.name}`
}

/** Convert table UI filters into canonical JSON Logic for creating a view. */
export function serializeDataViewFilters(
  filters: Partial<SupabaseTableHook<unknown>>,
  options: Pick<DataViewCompileOptions, 'customFields' | 'virtualTableScope'> & {
    source: DataViewSource
    excludedColumns?: readonly string[]
    dateTimeZone?: string
  }
): Record<string, unknown> {
  const excludedColumns = new Set([
    ...(options.excludedColumns ?? []),
    ...(options.virtualTableScope ? [getVirtualTableScopeColumn(options.virtualTableScope)] : [])
  ])
  const dateTimeZone = options.dateTimeZone ?? 'UTC'
  const predicates: Record<string, unknown>[] = []
  const add = (column: string, predicate: Record<string, unknown> | null) => {
    if (!excludedColumns.has(column) && predicate) predicates.push(predicate)
  }
  const fieldName = (column: string) => toDataViewFieldName(options.source, column)
  const normalizeColumnValue = (column: string, value: unknown) => {
    if (!isSourceDateField(options.source, column)) return value
    return Array.isArray(value)
      ? value.map(item => toIsoDateInTimeZone(item, dateTimeZone))
      : toIsoDateInTimeZone(value, dateTimeZone)
  }
  const addColumnPredicate = (column: string, operator: string, value: unknown) =>
    add(
      column,
      toDataViewPredicate(
        operator,
        fieldName(column),
        normalizeDataViewFilterValue(options.source, column, normalizeColumnValue(column, value))
      )
    )

  filters.eqFilters?.forEach(filter => addColumnPredicate(filter.column, 'eq', filter.value))
  filters.neqFilters?.forEach(filter => addColumnPredicate(filter.column, 'neq', filter.value))
  filters.ilikeFilters?.forEach(filter =>
    addColumnPredicate(filter.column, 'contains', filter.value)
  )
  filters.inFilters?.forEach(filter => addColumnPredicate(filter.column, 'in', filter.values))
  filters.gtFilters?.forEach(filter =>
    addColumnPredicate(filter.column, filter.orEqual ? 'gte' : 'gt', filter.value)
  )
  filters.ltFilters?.forEach(filter =>
    addColumnPredicate(filter.column, filter.orEqual ? 'lte' : 'lt', filter.value)
  )

  const comparisonFilters = Array.isArray(filters.filters)
    ? filters.filters
    : filters.filters
      ? [filters.filters]
      : []
  comparisonFilters.forEach(filter =>
    addColumnPredicate(filter.column, filter.operator, filter.value)
  )

  if (filters.notFilter?.[1] === 'in') {
    const [column, , rawValues] = filters.notFilter
    const canonicalField = fieldName(column)
    parseNotInValues(rawValues).forEach(value =>
      addColumnPredicate(column, 'neq', canonicalField.endsWith('team_id') ? Number(value) : value)
    )
  }

  filters.customFieldFilters?.forEach(filter => {
    const field = getCustomFieldReference(filter, options)
    const isDateField =
      options.customFields?.find(customField => customField.id === filter.customFieldId)?.type ===
      'date'
    const normalizeCustomValue = (value: unknown) =>
      isDateField ? toIsoDateInTimeZone(value, dateTimeZone) : value

    if (filter.operator === 'contains') {
      add(field, toDataViewPredicate('contains', field, filter.value))
    } else if (filter.operator === 'includes') {
      add(field, toDataViewPredicate('in', field, filter.values))
    } else if (filter.operator === 'excludes') {
      filter.values?.forEach(value => add(field, toDataViewPredicate('neq', field, value)))
    } else if (filter.operator === 'date_between') {
      add(field, toDataViewPredicate('gte', field, normalizeCustomValue(filter.start)))
      add(field, toDataViewPredicate('lte', field, normalizeCustomValue(filter.end)))
    } else if ('value' in filter) {
      add(field, toDataViewPredicate(filter.operator, field, normalizeCustomValue(filter.value)))
    }
  })

  if (predicates.length === 0) return {}
  return predicates.length === 1 ? predicates[0] : { and: predicates }
}

export const DataViewInputSchema = DataViewFieldsSchema.superRefine((value, context) => {
  if (value.source === 'kpi' && !value.kpis_table_id) {
    context.addIssue({ code: 'custom', path: ['kpis_table_id'], message: 'KPI table is required' })
  }
  if (value.source === 'custom_record' && !value.custom_record_table_id) {
    context.addIssue({
      code: 'custom',
      path: ['custom_record_table_id'],
      message: 'Record table is required'
    })
  }
  if (value.source !== 'kpi' && value.kpis_table_id) {
    context.addIssue({
      code: 'custom',
      path: ['kpis_table_id'],
      message: 'KPI scope is not valid for this source'
    })
  }
  if (value.source !== 'custom_record' && value.custom_record_table_id) {
    context.addIssue({
      code: 'custom',
      path: ['custom_record_table_id'],
      message: 'Record scope is not valid for this source'
    })
  }
  if (!isValidDataViewFilter(value.filters)) {
    context.addIssue({
      code: 'custom',
      path: ['filters'],
      message: 'Filters must be supported JSON Logic'
    })
  }
})

export const DataViewUpdateSchema = DataViewFieldsSchema.partial().superRefine((value, context) => {
  if (value.source === undefined) {
    if (value.kpis_table_id !== undefined && value.kpis_table_id !== null) {
      context.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'Source is required when updating KPI scope'
      })
    }
    if (value.custom_record_table_id !== undefined && value.custom_record_table_id !== null) {
      context.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'Source is required when updating record scope'
      })
    }
  }
  if (value.source === 'kpi') {
    if (!value.kpis_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['kpis_table_id'],
        message: 'KPI table is required'
      })
    }
    if (value.custom_record_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['custom_record_table_id'],
        message: 'Record scope is not valid for this source'
      })
    }
  } else if (value.source === 'custom_record') {
    if (!value.custom_record_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['custom_record_table_id'],
        message: 'Record table is required'
      })
    }
    if (value.kpis_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['kpis_table_id'],
        message: 'KPI scope is not valid for this source'
      })
    }
  } else if (value.source !== undefined) {
    if (value.kpis_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['kpis_table_id'],
        message: 'KPI scope is not valid for this source'
      })
    }
    if (value.custom_record_table_id) {
      context.addIssue({
        code: 'custom',
        path: ['custom_record_table_id'],
        message: 'Record scope is not valid for this source'
      })
    }
  }
  if (value.filters !== undefined && !isValidDataViewFilter(value.filters)) {
    context.addIssue({
      code: 'custom',
      path: ['filters'],
      message: 'Filters must be supported JSON Logic'
    })
  }
})

export type DataViewInput = z.infer<typeof DataViewInputSchema>

export class DataViewValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DataViewValidationError'
    Object.setPrototypeOf(this, DataViewValidationError.prototype)
  }
}

export default class DataViews {
  constructor(private readonly rpcClient: DolfinRpc) {}

  list(params: { organizationId: number; includeArchived?: boolean }): Promise<DataView[]> {
    return this.rpcClient.listDataViews(params)
  }

  private async getCompileOptions(
    organizationId: number,
    source: DataViewSource,
    kpisTableId?: number | null,
    customRecordTableId?: number | null
  ): Promise<DataViewCompileOptions> {
    const virtualTableId = kpisTableId ?? customRecordTableId ?? undefined
    const [fields, localCustomFields, parentDealCustomFields] = await Promise.all([
      this.rpcClient.getDataViewFilterFields({
        organizationId,
        source,
        virtualTableId
      }),
      this.rpcClient.getCustomFields(source, organizationId, virtualTableId),
      source === 'activity' || source === 'invoice'
        ? this.rpcClient.getCustomFields('deal', organizationId)
        : Promise.resolve([])
    ])
    return {
      fields,
      customFields: [...localCustomFields, ...parentDealCustomFields],
      virtualTableScope:
        source === 'kpi' && kpisTableId
          ? { kind: 'kpi', tableId: kpisTableId }
          : source === 'custom_record' && customRecordTableId
            ? { kind: 'custom_record', tableId: customRecordTableId }
            : null
    }
  }

  private async validateInput(organizationId: number, input: DataViewInput) {
    const options = await this.getCompileOptions(
      organizationId,
      input.source,
      input.kpis_table_id,
      input.custom_record_table_id
    )
    try {
      compileDataViewFilters(input.filters, input.source, options)
      const userField = toSourceGridField(input.source, input.user_field, options)
      const dateField = toSourceGridField(input.source, input.date_field, options)
      if (userField.valueType !== 'user') {
        throw new Error('User field must reference a user field')
      }
      if (dateField.valueType !== 'date') {
        throw new Error('Date field must reference a date field')
      }
    } catch (error) {
      throw new DataViewValidationError(
        error instanceof Error ? error.message : 'Invalid saved view configuration'
      )
    }
    return options
  }

  async create(params: {
    organizationId: number
    createdBy: string
    input: DataViewInput
  }): Promise<DataView> {
    const input = DataViewInputSchema.parse(params.input)
    const filters = canonicalizeSourceAwareJsonLogic(input.source, input.filters) as Record<
      string,
      unknown
    >
    const normalizedInput = { ...input, filters }
    await this.validateInput(params.organizationId, normalizedInput)
    const createParams: DataViewCreateParams = {
      ...normalizedInput,
      filters: filters as Json,
      organizationId: params.organizationId,
      createdBy: params.createdBy,
      archived_at: null
    }
    return this.rpcClient.createDataView(createParams)
  }

  async update(params: DataViewUpdateParams): Promise<DataView> {
    const updates = DataViewUpdateSchema.parse(params.updates)
    const current = (
      await this.rpcClient.listDataViews({
        organizationId: params.organizationId,
        includeArchived: true
      })
    ).find(view => view.id === params.id)
    if (!current) throw new Error('Data view not found')

    const source = (updates.source ?? current.source) as DataViewSource
    const filters = canonicalizeSourceAwareJsonLogic(
      source,
      updates.filters ?? current.filters
    ) as Record<string, unknown>
    const merged = DataViewInputSchema.parse({
      label: updates.label ?? current.label,
      source,
      kpis_table_id:
        updates.kpis_table_id !== undefined ? updates.kpis_table_id : current.kpis_table_id,
      custom_record_table_id:
        updates.custom_record_table_id !== undefined
          ? updates.custom_record_table_id
          : current.custom_record_table_id,
      filters,
      user_field: updates.user_field ?? current.user_field,
      date_field: updates.date_field ?? current.date_field
    })
    await this.validateInput(params.organizationId, merged)

    const normalizedUpdates = { ...updates } as DataViewUpdateParams['updates']
    if (updates.filters !== undefined || updates.source !== undefined) {
      normalizedUpdates.filters = filters as Json
    }
    return this.rpcClient.updateDataView({ ...params, updates: normalizedUpdates })
  }

  archive(params: { id: number; organizationId: number }): Promise<void> {
    return this.rpcClient.archiveDataView(params)
  }
}
